<template>
    <div class="col orders-table-vue">
        <div class="d-flex align-items-center mb-2">
            <div class="p-2 pr-5">
                <h4 class="mb-0 text-uppercase">{{ title }}</h4>
            </div>
            <div v-if="icons && icons.length" class="p-2 px-5 col-divided">
                <order-status-key
                    :icons="icons"
                    :with-links="true"
                    :user-role="_userType"
                ></order-status-key>
            </div>
            <div class="ml-auto select-blue">
                <multiselect
                    v-if="filterEnabled('days_past')"
                    v-model="filterDays"
                    :options="daysOptions"
                    :show-labels="false"
                    placeholder="Date Purchased"
                    track-by="value"
                    label="text"
                    @input="onFilterDaysChange"
                ></multiselect>
            </div>
            <div class="ml-1 select-blue">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    :allow-empty="false"
                    :show-labels="false"
                    @input="onPerPageChange"
                ></multiselect>
            </div>
        </div>
        <div class="row">
            <div class="col">
                <div class="jgile-table">
                    <div v-if="loading" class="text-center py-5">
                        <i class="fa fa-spinner fa-spin fa-2x"></i>
                    </div>
                    <table v-else class="table table-a">
                        <thead>
                        <tr>
                            <th :colspan="_isAdvertiser ? 2 : 1">
                                <sort-button
                                    field="uuid"
                                    label="Order #"
                                    :current-sort="sort"
                                    @sort="sortBy"
                                />
                            </th>
                            <th v-if="!_isAdvertiser">
                                <sort-button
                                    field="advertiser_name"
                                    label="Advertiser"
                                    :current-sort="sort"
                                    @sort="sortBy"
                                />
                            </th>
                            <th>
                                <sort-button
                                    field="assets_count"
                                    label="Assets"
                                    :current-sort="sort"
                                    @sort="sortBy"
                                />
                            </th>
                            <th>
                                <sort-button
                                    field="start_date"
                                    label="Run Date(s)"
                                    :current-sort="sort"
                                    @sort="sortBy"
                                />
                            </th>
                            <th>
                                <sort-button
                                    field="units"
                                    label="Units"
                                    :current-sort="sort"
                                    @sort="sortBy"
                                />
                            </th>
                            <th>
                                <sort-button
                                    field="amount"
                                    label="Price"
                                    :current-sort="sort"
                                    @sort="sortBy"
                                />
                            </th>
                            <th v-if="_isAdmin">
                                <button class="d-flex align-content-center align-items-center justify-content-between">
                                    Payment Status
                                </button>
                            </th>
                            <th colspan="2" class="actions"><span class="column-title"></span></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr class="row-spacer">
                            <td colspan="8"></td>
                        </tr>
                        </tbody>
                        <orders-table-order
                            v-for="order in orders"
                            :key="order.uuid + 'OrderRow'"
                            :order="order"
                            :user-role="_userType"
                            :disable-actions="disableActions"
                            :denied-only="deniedOnly"
                            @refresh="loadOrders"
                            @open-review="handleOpenReview"
                            @open-view-creative="handleOpenViewCreative"
                        />
                    </table>
                    <div v-if="!loading && orders.length === 0" class="text-center py-5">
                        <p class="text-muted">No orders found.</p>
                    </div>
                    <div v-if="pagination.last_page > 1" class="d-flex justify-content-between my-2">
                        <vue-pagination
                            :current-page="pagination.current_page"
                            :last-page="pagination.last_page"
                            @page-change="goToPage"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import OrdersTableApi from '@js/services/OrdersTableApi';
import SortButton from './partials/SortButton.vue';
import VuePagination from './partials/VuePagination.vue';
import OrdersTableOrder from './OrdersTableOrder.vue';

export default {
    name: 'OrdersTable',

    components: {
        SortButton,
        VuePagination,
        OrdersTableOrder,
    },

    props: {
        query: {
            type: Object,
            default: () => ({}),
        },
        icons: {
            type: Array,
            default: () => [],
        },
        title: {
            type: String,
            default: 'Orders',
        },
        enableFilters: {
            type: Array,
            default: () => [],
        },
        disableActions: {
            type: Boolean,
            default: false,
        },
        deniedOnly: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            orders: [],
            loading: true,
            perPage: 10,
            filterDays: null,
            sort: '-created_at',
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
            },
            perPageOptions: [10, 25, 50],
            daysOptions: [
                { text: 'Past 30 days', value: 30 },
                { text: 'Past 6 months', value: 180 },
                { text: 'Past year', value: 365 },
            ],
        };
    },

    mounted() {
        this.loadOrders(1);

        // Listen for refresh events from other components
        Bus.$on('refreshOrdersTable', this.refresh);

        // Listen for creative review closed to refresh counts
        window.addEventListener('creative-review-closed', this.refresh);
    },

    beforeDestroy() {
        Bus.$off('refreshOrdersTable', this.refresh);
        window.removeEventListener('creative-review-closed', this.refresh);
    },

    methods: {
        filterEnabled(filter) {
            return this.enableFilters.includes(filter);
        },

        async loadOrders(page = 1) {
            this.loading = true;

            try {
                const params = {
                    page,
                    perPage: this.perPage,
                    sort: this.sort,
                    ...this.query,
                };

                if (this.filterDays) {
                    params.filter = {
                        ...params.filter,
                        days_past: this.filterDays.value,
                    };
                }

                const response = await OrdersTableApi.getOrders(params);
                this.orders = response.data.data;
                this.pagination = {
                    current_page: response.data.meta.current_page,
                    last_page: response.data.meta.last_page,
                    per_page: response.data.meta.per_page,
                    total: response.data.meta.total,
                };
            } catch (error) {
                console.error('Failed to load orders:', error);
                this.orders = [];
            } finally {
                this.loading = false;
            }
        },

        sortBy(field) {
            if (this.sort === field) {
                this.sort = `-${field}`;
            } else if (this.sort === `-${field}`) {
                this.sort = field;
            } else {
                this.sort = field;
            }
            this.loadOrders(1);
        },

        goToPage(page) {
            this.loadOrders(page);
        },

        onPerPageChange() {
            this.loadOrders(1);
        },

        onFilterDaysChange() {
            this.loadOrders(1);
        },

        refresh() {
            this.loadOrders(this.pagination.current_page);
        },

        handleOpenReview(creativeIds, orderId, isOrder = false) {
            Bus.$emit('openReviewCreative', creativeIds, orderId, isOrder);
        },

        handleOpenViewCreative(creativeIds, orderId, isOrder = false) {
            console.log('ASDF');
            Bus.$emit('openViewCreative', creativeIds, orderId, isOrder);
        },
    },
};
</script>

<style scoped>
.orders-table-vue {
    min-height: 300px;
}
</style>
